import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LottieAnimation } from '@shared/components/LottieAnimation';
import { Column, Pagination, Table } from '@shared/components/Table';
import { SearchBar } from '@shared/components/SearchBar';
import { FilterBar } from '@shared/components/FilterBar';
import { FilterPopup } from '@shared/components/FilterPopup';
import { ToggleButton } from '@shared/components/ToggleButton';
import { Tooltip } from '@shared/components/Tooltip';
import { Snackbar } from '@shared/components/Snackbar';
import { Menu, MenuSize, MenuVariant } from '@shared/components/Menu';
import noServicetypeAnimation from '@/assets/lottie/noServicetypeAnimation.json';
import noResultsAnimation from '@/assets/lottie/noResultsAnimation.json';
import { UpdateStatusDialog } from '@/epics/adminManagment/pages/adminList/UpdateStatusDialog';
import { SERVICE_TYPE_LABELS } from '../../constants/labels';
import { useUpdateServiceTypeStatus } from '../../hooks/useUpdateServiceTypeStatus';
import {
  ServiceTypeStatus,
  type ServiceTypeListItem,
} from '../../types/serviceTypeTypes';
import { getServiceTypeStatusDialogConfig } from './serviceTypeStatusDialogConfig';
import { useServiceTypeList } from './useServiceTypeList';

const LIST_PATH = '/Typy-usług';
const CREATE_PATH = `${LIST_PATH}/Dodaj-typ-usługi`;
const VIEW_PATH = `${LIST_PATH}/Podgląd-typu-usługi`;
const EDIT_PATH = `${LIST_PATH}/Edycja-typu-usługi`;
const MIN_SPACE_BELOW_FOR_MENU = 70;

const formatCreatedAt = (isoString: string | null | undefined): string => {
  if (!isoString) {
    return '—';
  }
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const formatter = new Intl.DateTimeFormat('pl-PL', {
    timeZone: 'Europe/Warsaw',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;
  const hour = parts.find((p) => p.type === 'hour')?.value;
  const minute = parts.find((p) => p.type === 'minute')?.value;
  const second = parts.find((p) => p.type === 'second')?.value;
  return `${year}/${month}/${day}, ${hour}:${minute}:${second}`;
};

const ServiceTypeList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [successMessage] = useState(
    () =>
      (location.state as { successMessage?: string } | null)?.successMessage ??
      ''
  );
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(() =>
    Boolean(successMessage)
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterContainerRef = useRef<HTMLDivElement | null>(null);
  const [openMenuRowId, setOpenMenuRowId] = useState<string | null>(null);
  const [menuOpensUpward, setMenuOpensUpward] = useState(false);
  const menuAnchorRef = useRef<HTMLDivElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const tableScrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (successMessage) {
      window.history.replaceState({}, document.title);
    }
  }, [successMessage]);

  const {
    serviceTypes,
    loading,
    page,
    setPage,
    limit,
    totalCount,
    isNoFilterFetch,
    search,
    isSearchClearing,
    setSearch,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    limitError,
    handleLimitChange,
    refetchServiceTypes,
  } = useServiceTypeList();

  const {
    selectedServiceType,
    isDialogOpen,
    isSubmitting: isStatusSubmitting,
    handleOpenDialog: openStatusDialog,
    handleDialogClose: closeStatusDialog,
    handleConfirm: handleConfirmStatusUpdate,
    showSuccessSnackbar: showStatusSuccessSnackbar,
    setShowSuccessSnackbar: setShowStatusSuccessSnackbar,
    successMessage: statusSuccessMessage,
    showErrorSnackbar: showStatusErrorSnackbar,
    setShowErrorSnackbar: setShowStatusErrorSnackbar,
    errorMessage: statusErrorMessage,
  } = useUpdateServiceTypeStatus({ onSuccess: () => refetchServiceTypes() });

  const goToCreate = () => navigate(CREATE_PATH);

  const handleApplyFiltersAndClose = () => {
    handleApplyFilters();
    setIsFilterOpen(false);
  };

  const handleCloseFilter = useCallback(() => {
    setDateRange(appliedFilters.dateRange);
    setIsFilterOpen(false);
  }, [appliedFilters.dateRange, setDateRange]);

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        filterContainerRef.current &&
        !filterContainerRef.current.contains(target) &&
        !(target as Element).closest?.('[data-date-picker-portal]')
      ) {
        handleCloseFilter();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isFilterOpen, handleCloseFilter]);

  useEffect(() => {
    if (openMenuRowId === null) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setOpenMenuRowId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuRowId]);

  const prevOpenMenuRowIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (openMenuRowId !== null) {
      prevOpenMenuRowIdRef.current = openMenuRowId;
      const anchor = menuAnchorRef.current;
      if (anchor) {
        const firstMenuitem =
          anchor.querySelector<HTMLElement>('[role="menuitem"]');
        if (firstMenuitem) {
          requestAnimationFrame(() => firstMenuitem.focus());
        }
      }
    } else {
      const hadOpenMenu = prevOpenMenuRowIdRef.current !== null;
      prevOpenMenuRowIdRef.current = null;
      if (hadOpenMenu && menuTriggerRef.current) {
        requestAnimationFrame(() => menuTriggerRef.current?.focus());
      }
    }
  }, [openMenuRowId]);

  const appliedFilterCount = useMemo(() => {
    let count = 0;
    if ((appliedFilters.dateRange.startDate ?? '').trim()) {
      count += 1;
    }
    if ((appliedFilters.dateRange.endDate ?? '').trim()) {
      count += 1;
    }
    return count;
  }, [appliedFilters.dateRange]);

  const columns: Column<ServiceTypeListItem>[] = useMemo(
    () => [
      {
        key: 'service_name',
        header: SERVICE_TYPE_LABELS.COLUMN_NAME,
        width: '350px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.service_name}>
              <span className="text-[#181A1B] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.service_name}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'description',
        header: SERVICE_TYPE_LABELS.COLUMN_DESCRIPTION,
        width: '600px',
        render: (row) => (
          <div className="flex items-center w-full min-w-0">
            <Tooltip content={row.description} align="center">
              <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] overflow-hidden text-ellipsis whitespace-nowrap block">
                {row.description}
              </span>
            </Tooltip>
          </div>
        ),
      },
      {
        key: 'created_at',
        header: SERVICE_TYPE_LABELS.COLUMN_CREATED_AT,
        // width: '200px',
        render: (row) => (
          <span className="text-[#474A4C] text-sm leading-5 tracking-[0.25px] whitespace-nowrap block">
            {formatCreatedAt(row.created_at)}
          </span>
        ),
      },
      {
        key: 'status',
        header: SERVICE_TYPE_LABELS.COLUMN_STATUS,
        width: '200px',
        render: (row, index) => (
          <div
            data-testid={`toggle-status-${index}`}
            onClick={(e) => {
              e.stopPropagation();
              openStatusDialog({
                id: row.id,
                service_name: row.service_name,
                status: row.status,
              });
            }}
            className="cursor-pointer inline-flex"
          >
            <div style={{ pointerEvents: 'none' }}>
              <ToggleButton
                checked={row.status === ServiceTypeStatus.Active}
                onChange={() => {}}
                label={
                  row.status === ServiceTypeStatus.Active
                    ? SERVICE_TYPE_LABELS.STATUS_ACTIVE
                    : SERVICE_TYPE_LABELS.STATUS_INACTIVE
                }
                size="Small"
              />
            </div>
          </div>
        ),
      },
      {
        key: 'actions',
        header: SERVICE_TYPE_LABELS.COLUMN_ACTIONS,
        width: '80px',
        stickyRight: true,
        stickyRightBg: '#ffffff',
        render: (row, index) => {
          const isOpen = openMenuRowId === row.id;
          return (
            <div
              ref={isOpen ? menuAnchorRef : undefined}
              className="relative inline-flex items-center justify-center w-full"
            >
              <button
                ref={(el) => {
                  if (isOpen) {
                    menuTriggerRef.current = el;
                  }
                }}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  const willOpen = openMenuRowId !== row.id;
                  if (willOpen) {
                    menuTriggerRef.current = e.currentTarget;
                    const buttonRect = (
                      e.currentTarget as HTMLButtonElement
                    ).getBoundingClientRect();
                    const containerRect =
                      tableScrollContainerRef.current?.getBoundingClientRect();
                    const visibleBottom = containerRect
                      ? containerRect.bottom
                      : window.innerHeight;
                    const spaceBelow = visibleBottom - buttonRect.bottom;
                    setMenuOpensUpward(spaceBelow < MIN_SPACE_BELOW_FOR_MENU);
                  }
                  setOpenMenuRowId((prev) => (prev === row.id ? null : row.id));
                }}
                aria-label={`${SERVICE_TYPE_LABELS.COLUMN_ACTIONS} ${row.service_name}`}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                data-testid={`action-menu-button-${index ?? 0}`}
                className="flex items-center justify-center w-10 h-10 rounded-full hover:bg-gray-100 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z"
                    stroke="#474A4C"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {isOpen && (
                <div
                  className={`absolute left-1/3 z-50 -translate-x-1/2 ${menuOpensUpward ? 'bottom-full mb-1' : 'top-full mt-1'}`}
                  role="presentation"
                  onKeyDown={(e) => {
                    if (e.key !== 'Tab') {
                      return;
                    }
                    const anchor = menuAnchorRef.current;
                    if (!anchor) {
                      return;
                    }
                    const items =
                      anchor.querySelectorAll<HTMLElement>('[role="menuitem"]');
                    const lastItem = items[items.length - 1];
                    if (
                      lastItem &&
                      document.activeElement === lastItem &&
                      !e.shiftKey
                    ) {
                      setOpenMenuRowId(null);
                    }
                  }}
                >
                  <Menu
                    items={[
                      { id: 'VIEW', label: 'Podgląd' },
                      { id: 'EDIT', label: 'Edytuj' },
                    ]}
                    variant={MenuVariant.Light}
                    size={MenuSize.Small}
                    toolTip={false}
                    onItemClick={(itemId) => {
                      setOpenMenuRowId(null);
                      if (itemId === 'VIEW') {
                        navigate(`${VIEW_PATH}/${row.id}`);
                      } else if (itemId === 'EDIT') {
                        navigate(`${EDIT_PATH}/${row.id}`);
                      }
                    }}
                    showLeadingIcons={false}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ],
    [openMenuRowId, menuOpensUpward, navigate, openStatusDialog]
  );

  const pageOutOfRange =
    !loading && totalCount > 0 && page > Math.ceil(totalCount / limit);

  const hasSearch = search.trim() !== '';
  const hasDateFilter =
    (appliedFilters.dateRange.startDate ?? '').trim() !== '' ||
    (appliedFilters.dateRange.endDate ?? '').trim() !== '';

  const hasNoData =
    !loading &&
    !isSearchClearing &&
    !pageOutOfRange &&
    page === 1 &&
    serviceTypes.length === 0 &&
    totalCount === 0 &&
    isNoFilterFetch &&
    !hasSearch &&
    !hasDateFilter;

  const showNoResults =
    !loading &&
    !isSearchClearing &&
    !hasNoData &&
    serviceTypes.length === 0 &&
    (hasSearch || hasDateFilter);

  const noResultsDescription =
    hasSearch && hasDateFilter
      ? SERVICE_TYPE_LABELS.NO_RESULTS_DESCRIPTION_BOTH
      : hasSearch
        ? SERVICE_TYPE_LABELS.NO_RESULTS_DESCRIPTION_SEARCH
        : SERVICE_TYPE_LABELS.NO_RESULTS_DESCRIPTION_FILTER;

  return (
    <div className="bg-white w-full h-full min-h-0 flex flex-col p-4 rounded shadow-[0px_2px_4px_0px_#00000014] gap-3 overflow-hidden">
      <div className="flex items-center p-1 justify-between shrink-0 mb-3">
        <p className="text-titleLarge text-neutral-10">
          {SERVICE_TYPE_LABELS.LIST_TITLE}
        </p>
        {!hasNoData && (
          <button
            type="button"
            onClick={goToCreate}
            data-testid="Dodaj typ usługi-button"
            className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
            aria-label={SERVICE_TYPE_LABELS.BUTTON_CREATE}
          >
            <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
              {SERVICE_TYPE_LABELS.BUTTON_CREATE}
            </span>
          </button>
        )}
      </div>

      {!hasNoData && (
        <div className="flex items-center justify-between shrink-0">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder={SERVICE_TYPE_LABELS.SEARCH_PLACEHOLDER}
          />
          <div className="flex items-center gap-2">
            <div
              ref={filterContainerRef}
              className={`dropdown ${isFilterOpen ? 'show' : ''}`}
            >
              <FilterBar
                onClick={() => setIsFilterOpen((prev) => !prev)}
                isOpen={isFilterOpen}
                filterCount={appliedFilterCount}
              />
              <FilterPopup
                isOpen={isFilterOpen}
                onClose={handleCloseFilter}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                showDateRange
                onApply={handleApplyFiltersAndClose}
                onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>
      )}

      <div
        ref={tableScrollContainerRef}
        className="flex-1 min-h-0 overflow-x-auto overflow-y-auto"
      >
        {hasNoData ? (
          <div className="flex flex-col items-center justify-center w-full h-full">
            <LottieAnimation
              animationData={noServicetypeAnimation}
              width={372}
              aspectRatio="372 / 294"
            />
            <div className="flex flex-col items-center justify-center gap-4">
              <div className="flex flex-col items-start justify-start gap-1 mt-12">
                <div className="w-[548px] text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                  {SERVICE_TYPE_LABELS.EMPTY_STATE_TITLE}
                </div>
                <div className="w-[548px] text-center text-[#474A4C] text-base font-normal leading-6 tracking-[0.5px]">
                  {SERVICE_TYPE_LABELS.EMPTY_STATE_DESCRIPTION}
                </div>
              </div>
              <button
                type="button"
                onClick={goToCreate}
                data-testid="Dodaj typ usługi-button"
                className="h-10 bg-[#2A4455] px-4 py-2.5 cursor-pointer rounded-lg hover:bg-[#1f3340] transition-colors flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2A4455]"
                aria-label={SERVICE_TYPE_LABELS.BUTTON_CREATE}
              >
                <span className="text-white text-sm font-medium leading-5 tracking-[0.1px]">
                  {SERVICE_TYPE_LABELS.BUTTON_CREATE}
                </span>
              </button>
            </div>
          </div>
        ) : showNoResults ? (
          <div className="flex flex-col items-center justify-center w-full h-full gap-3">
            <LottieAnimation
              animationData={noResultsAnimation}
              width="clamp(160px, 18vw, 360px)"
              aspectRatio="432 / 350"
            />
            <div className="w-[548px] max-w-full h-12 flex flex-col items-center justify-start gap-1">
              <div className="w-full h-6 text-center text-[#474A4C] text-base font-medium leading-6 tracking-[0.5px]">
                {SERVICE_TYPE_LABELS.NO_RESULTS_TITLE}
              </div>
              <div className="w-full h-5 text-center text-[#474A4C] text-sm font-normal leading-5 tracking-[0.25px]">
                {noResultsDescription}
              </div>
            </div>
          </div>
        ) : (
          <Table
            columns={columns}
            data={serviceTypes}
            loading={loading}
            loadingRows={limit}
            stickyHeader
            getRowKey={(row) => row.id}
            getStickyCellZIndex={(row, _idx, columnKey) =>
              columnKey === 'actions' && openMenuRowId === row.id ? 50 : 1
            }
          />
        )}
      </div>

      {!hasNoData && !showNoResults && totalCount > 10 && (
        <div className="shrink-0 px-4 -mx-4">
          <Pagination
            page={page}
            limit={limit}
            totalCount={totalCount}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
          {limitError && (
            <div className="mt-2 text-[#E11B1B] text-sm font-medium leading-5 tracking-[0.1px]">
              {limitError}
            </div>
          )}
        </div>
      )}

      <Snackbar
        open={successSnackbarOpen}
        message={successMessage}
        variant="success"
        onClose={() => setSuccessSnackbarOpen(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showStatusSuccessSnackbar}
        message={statusSuccessMessage}
        variant="success"
        onClose={() => setShowStatusSuccessSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />
      <Snackbar
        open={showStatusErrorSnackbar}
        message={statusErrorMessage}
        variant="error"
        onClose={() => setShowStatusErrorSnackbar(false)}
        autoHideDuration={10000}
        position="top-center"
        showCloseButton
      />

      {selectedServiceType && (
        <UpdateStatusDialog
          open={isDialogOpen}
          onClose={closeStatusDialog}
          onConfirm={handleConfirmStatusUpdate}
          isSubmitting={isStatusSubmitting}
          {...getServiceTypeStatusDialogConfig(
            selectedServiceType.service_name,
            selectedServiceType.status === ServiceTypeStatus.Active
          )}
        />
      )}
    </div>
  );
};

export default ServiceTypeList;
